// Copy for the hero growth funnel (HeroFunnel.jsx), per market locale.
// Keys mirror `locale` in src/data/markets.js; English is the fallback.
// Band order is fixed: awareness, acquisition, retention, revenue.

const EN = {
  title: "How AppFillip grows an app",
  step: "Step",
  bands: [
    {
      label: "Awareness",
      does: "ASO · Influencer · Creative",
      metricLabel: "impressions",
      explain:
        "We get your app seen. ASO, store creatives and influencer campaigns put it in front of the right users on the App Store and Google Play.",
    },
    {
      label: "Acquisition",
      does: "Apple Ads · Meta · TikTok",
      metricLabel: "installs",
      explain:
        "We turn views into installs. Apple Search Ads, Meta and TikTok campaigns, plus store-page A/B tests that lift conversion.",
    },
    {
      label: "Retention",
      does: "Lifecycle · Ratings · Analytics",
      metricLabel: "D30 retained",
      explain:
        "We keep users coming back. Lifecycle messaging, ratings & review programs and analytics that show what to fix next.",
    },
    {
      label: "Revenue",
      does: "Organic-first growth",
      metricLabel: "ROAS",
      explain:
        "The result: a category leader that compounds — more organic installs, lower CPI, and revenue that grows month after month.",
    },
  ],
};

const I18N = {
  ja_JP: {
    title: "AppFillipがアプリを成長させる仕組み",
    step: "ステップ",
    bands: [
      { label: "認知", does: "ASO · インフルエンサー · クリエイティブ", metricLabel: "インプレッション", explain: "アプリを見つけてもらう。ASO、ストアクリエイティブ、インフルエンサー施策で、App StoreとGoogle Playの適切なユーザーにリーチします。" },
      { label: "獲得", does: "Apple Ads · Meta · TikTok", metricLabel: "インストール", explain: "閲覧をインストールに変える。Apple Search Ads、Meta、TikTokの広告と、コンバージョンを高めるストアページのA/Bテスト。" },
      { label: "継続", does: "ライフサイクル · 評価 · 分析", metricLabel: "30日継続率", explain: "ユーザーを定着させる。ライフサイクルメッセージ、評価・レビュー施策、次に直すべき点が分かる分析。" },
      { label: "収益", does: "オーガニック主導の成長", metricLabel: "ROAS", explain: "結果：カテゴリーリーダーとして複利的に成長。オーガニックインストールが増え、CPIが下がり、収益が毎月伸びます。" },
    ],
  },
  de_DE: {
    title: "So lässt AppFillip Apps wachsen",
    step: "Schritt",
    bands: [
      { label: "Bekanntheit", does: "ASO · Influencer · Creatives", metricLabel: "Impressionen", explain: "Wir machen Ihre App sichtbar. ASO, Store-Creatives und Influencer-Kampagnen bringen sie vor die richtigen Nutzer im App Store und bei Google Play." },
      { label: "Akquise", does: "Apple Ads · Meta · TikTok", metricLabel: "Installs", explain: "Wir machen aus Aufrufen Installationen. Apple Search Ads, Meta- und TikTok-Kampagnen plus A/B-Tests der Store-Seite, die die Conversion steigern." },
      { label: "Bindung", does: "Lifecycle · Bewertungen · Analytics", metricLabel: "D30-Bindung", explain: "Wir halten Nutzer bei der Stange. Lifecycle-Messaging, Bewertungs- und Review-Programme und Analytics, die zeigen, was als Nächstes zu tun ist." },
      { label: "Umsatz", does: "Organisches Wachstum zuerst", metricLabel: "ROAS", explain: "Das Ergebnis: ein Kategorieführer, der sich verstärkt — mehr organische Installs, niedrigerer CPI und Umsatz, der Monat für Monat wächst." },
    ],
  },
  fr_FR: {
    title: "Comment AppFillip fait grandir une app",
    step: "Étape",
    bands: [
      { label: "Notoriété", does: "ASO · Influence · Créa", metricLabel: "impressions", explain: "Nous rendons votre app visible. ASO, visuels store et campagnes d'influence la placent devant les bons utilisateurs sur l'App Store et Google Play." },
      { label: "Acquisition", does: "Apple Ads · Meta · TikTok", metricLabel: "installations", explain: "Nous transformons les vues en installations. Campagnes Apple Search Ads, Meta et TikTok, plus des tests A/B de la fiche store qui augmentent la conversion." },
      { label: "Rétention", does: "Lifecycle · Avis · Analytics", metricLabel: "rétention J30", explain: "Nous fidélisons vos utilisateurs. Messages lifecycle, programmes d'avis et de notes, et analytics qui montrent quoi corriger ensuite." },
      { label: "Revenus", does: "Croissance organique d'abord", metricLabel: "ROAS", explain: "Le résultat : un leader de catégorie qui s'auto-alimente — plus d'installations organiques, un CPI plus bas et des revenus qui croissent chaque mois." },
    ],
  },
  es_ES: {
    title: "Cómo AppFillip hace crecer una app",
    step: "Paso",
    bands: [
      { label: "Visibilidad", does: "ASO · Influencers · Creatividades", metricLabel: "impresiones", explain: "Hacemos que encuentren tu app. ASO, creatividades de tienda y campañas con influencers la ponen delante de los usuarios adecuados en App Store y Google Play." },
      { label: "Adquisición", does: "Apple Ads · Meta · TikTok", metricLabel: "instalaciones", explain: "Convertimos visitas en instalaciones. Campañas en Apple Search Ads, Meta y TikTok, más tests A/B de la ficha que elevan la conversión." },
      { label: "Retención", does: "Lifecycle · Reseñas · Analítica", metricLabel: "retención D30", explain: "Hacemos que los usuarios vuelvan. Mensajes de lifecycle, programas de valoraciones y reseñas, y analítica que muestra qué arreglar después." },
      { label: "Ingresos", does: "Crecimiento orgánico primero", metricLabel: "ROAS", explain: "El resultado: un líder de categoría que se retroalimenta — más instalaciones orgánicas, menor CPI e ingresos que crecen mes a mes." },
    ],
  },
  pt_BR: {
    title: "Como a AppFillip faz um app crescer",
    step: "Etapa",
    bands: [
      { label: "Descoberta", does: "ASO · Influenciadores · Criativos", metricLabel: "impressões", explain: "Fazemos seu app ser encontrado. ASO, criativos de loja e campanhas com influenciadores o colocam na frente dos usuários certos na App Store e no Google Play." },
      { label: "Aquisição", does: "Apple Ads · Meta · TikTok", metricLabel: "instalações", explain: "Transformamos visitas em instalações. Campanhas no Apple Search Ads, Meta e TikTok, além de testes A/B da página da loja que aumentam a conversão." },
      { label: "Retenção", does: "Lifecycle · Avaliações · Analytics", metricLabel: "retenção D30", explain: "Mantemos os usuários voltando. Mensagens de lifecycle, programas de avaliações e analytics que mostram o que corrigir em seguida." },
      { label: "Receita", does: "Crescimento orgânico primeiro", metricLabel: "ROAS", explain: "O resultado: um líder de categoria que se retroalimenta — mais instalações orgânicas, CPI menor e receita crescendo mês a mês." },
    ],
  },
  th_TH: {
    title: "AppFillip ทำให้แอปเติบโตได้อย่างไร",
    step: "ขั้นตอน",
    bands: [
      { label: "การรับรู้", does: "ASO · อินฟลูเอนเซอร์ · ครีเอทีฟ", metricLabel: "อิมเพรสชัน", explain: "เราทำให้แอปของคุณถูกค้นพบ ASO ครีเอทีฟบนสโตร์ และแคมเปญอินฟลูเอนเซอร์ พาแอปไปอยู่ตรงหน้าผู้ใช้ที่ใช่บน App Store และ Google Play" },
      { label: "การหาผู้ใช้", does: "Apple Ads · Meta · TikTok", metricLabel: "การติดตั้ง", explain: "เราเปลี่ยนการเข้าชมเป็นการติดตั้ง ด้วยแคมเปญ Apple Search Ads, Meta และ TikTok พร้อม A/B test หน้าสโตร์ที่เพิ่มอัตราการแปลง" },
      { label: "การรักษาผู้ใช้", does: "ไลฟ์ไซเคิล · เรตติ้ง · การวิเคราะห์", metricLabel: "คงอยู่ D30", explain: "เราทำให้ผู้ใช้กลับมา ด้วยข้อความตามไลฟ์ไซเคิล โปรแกรมเรตติ้งและรีวิว และการวิเคราะห์ที่บอกว่าควรแก้อะไรต่อไป" },
      { label: "รายได้", does: "เติบโตแบบออร์แกนิกเป็นหลัก", metricLabel: "ROAS", explain: "ผลลัพธ์: ผู้นำหมวดหมู่ที่เติบโตทบต้น — ติดตั้งออร์แกนิกมากขึ้น CPI ต่ำลง และรายได้ที่เพิ่มขึ้นทุกเดือน" },
    ],
  },
  ko_KR: {
    title: "AppFillip이 앱을 성장시키는 방법",
    step: "단계",
    bands: [
      { label: "인지", does: "ASO · 인플루언서 · 크리에이티브", metricLabel: "노출", explain: "앱이 발견되게 합니다. ASO, 스토어 크리에이티브, 인플루언서 캠페인으로 App Store와 Google Play에서 적합한 사용자에게 노출합니다." },
      { label: "획득", does: "Apple Ads · Meta · TikTok", metricLabel: "설치", explain: "조회를 설치로 바꿉니다. Apple Search Ads, Meta, TikTok 캠페인과 전환율을 높이는 스토어 페이지 A/B 테스트." },
      { label: "유지", does: "라이프사이클 · 평점 · 분석", metricLabel: "D30 유지율", explain: "사용자가 다시 돌아오게 합니다. 라이프사이클 메시지, 평점·리뷰 프로그램, 다음에 고칠 점을 보여주는 분석." },
      { label: "수익", does: "오가닉 우선 성장", metricLabel: "ROAS", explain: "결과: 복리로 성장하는 카테고리 리더 — 오가닉 설치 증가, CPI 하락, 매달 늘어나는 수익." },
    ],
  },
};

/** Funnel copy for a market locale (e.g. "de_DE"); English when unknown. */
export function funnelCopy(locale) {
  return I18N[locale] || EN;
}
